import { ConfigurationKey, ConfigurationSchema, ConfigurationValue } from "@salon/types";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

interface ConfigurationState {
  // Configuration data by salon ID
  configurationsBySalon: Record<string, Partial<ConfigurationSchema>>;

  // Loading states by salon ID
  loadingStates: Record<string, boolean>;

  // Error states by salon ID
  errors: Record<string, string | null>;

  // Subscription management
  activeSubscriptions: Set<string>;
}

interface ConfigurationActions {
  // Configuration management
  setConfigurations: (salonId: string, configs: Partial<ConfigurationSchema>) => void;
  updateConfiguration: <K extends ConfigurationKey>(
    salonId: string,
    key: K,
    value: ConfigurationValue<K>
  ) => void;
  getConfiguration: <K extends ConfigurationKey>(
    salonId: string,
    key: K
  ) => ConfigurationValue<K> | undefined;

  // Loading and error states
  setLoading: (salonId: string, loading: boolean) => void;
  setError: (salonId: string, error: string | null) => void;

  // Salon management
  clearSalon: (salonId: string) => void;

  // Subscription management
  addSubscription: (subscriptionKey: string) => void;
  removeSubscription: (subscriptionKey: string) => void;
}

type ConfigurationStore = ConfigurationState & ConfigurationActions;

/**
 * Zustand store for managing salon configurations with real-time updates
 *
 * Features:
 * - Type-safe configuration access using ConfigurationSchema
 * - Per-salon configuration storage
 * - Loading and error state management
 * - Real-time subscription tracking
 * - Automatic cleanup when salons are switched
 */
export const useConfigStore = create<ConfigurationStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    configurationsBySalon: {},
    loadingStates: {},
    errors: {},
    activeSubscriptions: new Set(),

    // Configuration management
    setConfigurations: (salonId: string, configs: Partial<ConfigurationSchema>) => {
      set((state) => {
        const currentConfigs = state.configurationsBySalon[salonId] || {};

        // Check if there are actual changes to prevent unnecessary updates
        const hasChanges = Object.keys(configs).some(
          (key) =>
            currentConfigs[key as keyof ConfigurationSchema] !==
            configs[key as keyof ConfigurationSchema]
        );

        if (!hasChanges) {
          return state; // Return current state if no changes
        }

        return {
          configurationsBySalon: {
            ...state.configurationsBySalon,
            [salonId]: {
              ...currentConfigs,
              ...configs,
            },
          },
        };
      });
    },

    updateConfiguration: <K extends ConfigurationKey>(
      salonId: string,
      key: K,
      value: ConfigurationValue<K>
    ) => {
      set((state) => ({
        configurationsBySalon: {
          ...state.configurationsBySalon,
          [salonId]: {
            ...state.configurationsBySalon[salonId],
            [key]: value,
          },
        },
      }));
    },

    getConfiguration: <K extends ConfigurationKey>(
      salonId: string,
      key: K
    ): ConfigurationValue<K> | undefined => {
      const salonConfigs = get().configurationsBySalon[salonId];
      return salonConfigs?.[key] as ConfigurationValue<K> | undefined;
    },

    // Loading and error states
    setLoading: (salonId: string, loading: boolean) => {
      set((state) => ({
        loadingStates: {
          ...state.loadingStates,
          [salonId]: loading,
        },
      }));
    },

    setError: (salonId: string, error: string | null) => {
      set((state) => ({
        errors: {
          ...state.errors,
          [salonId]: error,
        },
      }));
    },

    // Salon management
    clearSalon: (salonId: string) => {
      set((state) => {
        const newConfigurationsBySalon = { ...state.configurationsBySalon };
        const newLoadingStates = { ...state.loadingStates };
        const newErrors = { ...state.errors };

        delete newConfigurationsBySalon[salonId];
        delete newLoadingStates[salonId];
        delete newErrors[salonId];

        return {
          configurationsBySalon: newConfigurationsBySalon,
          loadingStates: newLoadingStates,
          errors: newErrors,
        };
      });
    },

    // Subscription management
    addSubscription: (subscriptionKey: string) => {
      set((state) => ({
        activeSubscriptions: new Set(state.activeSubscriptions).add(subscriptionKey),
      }));
    },

    removeSubscription: (subscriptionKey: string) => {
      set((state) => {
        const newSubscriptions = new Set(state.activeSubscriptions);
        newSubscriptions.delete(subscriptionKey);
        return { activeSubscriptions: newSubscriptions };
      });
    },
  }))
);

// Selectors for easy access to specific salon data with shallow comparison
export const useConfigStoreSelectors = {
  // Get configurations for a specific salon
  useSalonConfigurations: (salonId: string) =>
    useConfigStore(
      (state) => state.configurationsBySalon[salonId] || {},
      (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => a[key] === b[key]);
      }
    ),

  // Get loading state for a salon
  useSalonLoading: (salonId: string) =>
    useConfigStore((state) => state.loadingStates[salonId] || false),

  // Get error state for a salon
  useSalonError: (salonId: string) => useConfigStore((state) => state.errors[salonId] || null),

  // Get specific configuration value
  useConfigurationValue: <K extends ConfigurationKey>(salonId: string, key: K) =>
    useConfigStore(
      (state) => state.configurationsBySalon[salonId]?.[key] as ConfigurationValue<K> | undefined
    ),
};
